import { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import type { MaturityDistributionLevel } from '../../graphql/types';
import './MaturityDistributionChart.css';

export interface MaturityDistributionChartProps {
  levelCounts: MaturityDistributionLevel[];
  averageScore: number;
  totalSubjects: number;
  width?: number;
  height?: number;
}

const MARGIN = { top: 24, right: 32, bottom: 48, left: 48 };

/** Map maturity level (1–6) to a semantic color. */
function levelColor(level: number): string {
  switch (level) {
    case 1:
      return '#d62728';
    case 2:
      return '#e8722a';
    case 3:
      return '#e8be2a';
    case 4:
      return '#8cc63f';
    case 5:
      return '#2ca02c';
    case 6:
      return '#1a7f1a';
    default:
      return '#9467bd';
  }
}

export function MaturityDistributionChart({
  levelCounts,
  averageScore,
  totalSubjects,
  width = 480,
  height = 260,
}: MaturityDistributionChartProps): JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || levelCounts.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = height - MARGIN.top - MARGIN.bottom;

    // Sort by level ascending
    const sorted = [...levelCounts].sort((a, b) => a.level - b.level);

    const levels = sorted.map((d) => String(d.level));
    const maxCount = d3.max(sorted, (d) => d.count) ?? 1;

    const xScale = d3.scaleBand().domain(levels).range([0, innerW]).padding(0.25);

    const yScale = d3
      .scaleLinear()
      .domain([0, Math.ceil(maxCount * 1.15)])
      .range([innerH, 0])
      .nice();

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    // Grid lines
    const yTicks = yScale.ticks(5);
    g.selectAll('.dist-grid')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => yScale(d))
      .attr('y2', (d) => yScale(d))
      .attr('stroke', 'var(--forge-theme-border, #e0e0e0)')
      .attr('stroke-dasharray', '3,3')
      .attr('stroke-width', 0.7);

    // X axis
    g.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(d3.axisBottom(xScale).tickFormat((d) => `Level ${d}`))
      .selectAll('text')
      .attr('font-size', '11px')
      .attr('fill', 'var(--forge-theme-text-medium, #757575)');

    // Y axis
    g.append('g')
      .call(d3.axisLeft(yScale).ticks(5))
      .selectAll('text')
      .attr('font-size', '11px')
      .attr('fill', 'var(--forge-theme-text-medium, #757575)');

    // Bars
    sorted.forEach((d) => {
      const bx = xScale(String(d.level)) ?? 0;
      const bw = xScale.bandwidth();
      const by = yScale(d.count);
      const bh = innerH - yScale(d.count);

      if (bh <= 0) return;

      const barG = g.append('g').attr('class', 'dist-bar');

      barG
        .append('rect')
        .attr('x', bx)
        .attr('y', by)
        .attr('width', bw)
        .attr('height', bh)
        .attr('fill', levelColor(d.level))
        .attr('rx', 3)
        .attr('ry', 3);

      barG
        .append('title')
        .text(
          `Level ${d.level}: ${d.count} subject${d.count !== 1 ? 's' : ''} (${d.percentage.toFixed(0)}%)`
        );

      // Count label on top of bar
      if (d.count > 0) {
        barG
          .append('text')
          .attr('x', bx + bw / 2)
          .attr('y', by - 4)
          .attr('text-anchor', 'middle')
          .attr('font-size', '11px')
          .attr('font-weight', '600')
          .attr('fill', 'var(--forge-theme-text-primary, #212121)')
          .text(String(d.count));

        // Percentage below count
        barG
          .append('text')
          .attr('x', bx + bw / 2)
          .attr('y', by - 4 + 14)
          .attr('text-anchor', 'middle')
          .attr('font-size', '9px')
          .attr('fill', 'var(--forge-theme-text-medium, #757575)')
          .text(`${d.percentage.toFixed(0)}%`);
      }
    });

    // Average score vertical indicator (dashed line at avg level position on x axis)
    if (averageScore > 0) {
      // Map avgScore to pixel position: interpolate between band centers
      const avgBandCenter = xScale(String(Math.round(averageScore)));
      if (avgBandCenter !== undefined) {
        const avgX = avgBandCenter + xScale.bandwidth() / 2;
        g.append('line')
          .attr('x1', avgX)
          .attr('x2', avgX)
          .attr('y1', 0)
          .attr('y2', innerH)
          .attr('stroke', '#1f77b4')
          .attr('stroke-width', 1.5)
          .attr('stroke-dasharray', '4,3');

        g.append('text')
          .attr('x', avgX + 4)
          .attr('y', 12)
          .attr('font-size', '10px')
          .attr('fill', '#1f77b4')
          .text(`avg ${averageScore.toFixed(1)}`);
      }
    }
  }, [levelCounts, averageScore, totalSubjects, width, height]);

  return (
    <div className="maturity-distribution-chart">
      <svg
        ref={svgRef}
        width={width}
        height={height}
        role="img"
        aria-label={`Maturity distribution bar chart: ${totalSubjects} subjects across levels 1 to 6, average score ${averageScore.toFixed(1)}`}
      />
    </div>
  );
}
